import {
  INVOICE_MATCH_RESULT_LABELS,
  type InvoiceMatch,
  type InvoiceMatchLine,
  MATCH_LINE_STATUS_LABELS,
} from "@station/contracts";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Undo2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalRequestPanel } from "../../../../features/approvals/request-panel.js";
import { ReasonDialog } from "../../../../features/counterparties/shared.js";
import { Section } from "../../../../features/expenses/shared.js";
import { Th } from "../../../../features/fuel/shared.js";
import {
  formatStockQuantity,
  moneyExport,
  quantityExport,
} from "../../../../features/market/market-values.js";
import {
  isDifferenceLine,
  matchActions,
  matchLineLabel,
  matchReportSummary,
  tolerancesText,
} from "../../../../features/market/match-values.js";
import { useInvoiceMatch } from "../../../../features/market/purchasing-queries.js";
import { useMarketPermissions, useRefreshMarket } from "../../../../features/market/queries.js";
import {
  crumbLink,
  Field,
  InvoiceMatchStatusBadge,
  MarketPage,
  MatchLineStatusBadge,
  TableFrame,
} from "../../../../features/market/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel } from "../../../../lib/excel.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/market/eslestirme/$id")({ component: InvoiceMatchRoute });

function InvoiceMatchRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const match = useInvoiceMatch(id);
  const title = match.data
    ? t("market.matches.detailTitleWithNo", { no: match.data.invoiceNo ?? t("market.matches.noInvoiceNo") })
    : t("market.matches.detailTitle");
  return (
    <MarketPage
      title={title}
      breadcrumb={[crumbLink(t("menu.marketInvoiceMatches"), "/market/eslestirme"), { label: title }]}
    >
      {() =>
        match.error ? (
          <Alert tone="danger">{errorMessage(match.error)}</Alert>
        ) : !match.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <InvoiceMatchDetail match={match.data} />
        )
      }
    </MarketPage>
  );
}

/**
 * Fark raporu (Prompt 16 — web, aşama B; ADR 0016 §9–10): ürün bazında sipariş / kabul / fatura miktarı ve birim
 * fiyatı, miktar / fiyat / tutar farkı ve satır durumu; ürünsüz fatura satırı (nakliye vb.) "sipariş / kabul dışı"
 * olarak ayrıca görünür. Kesin maliyet farkı (`costAdjustment`) tam eşleşmede hemen, farklı eşleşmede onayda
 * uygulanır; geri alma her şeyi tersler (gerekçeyle, `market.invoice.match`).
 */
function InvoiceMatchDetail({ match }: { match: InvoiceMatch }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const permissions = useMarketPermissions();
  const [unmatching, setUnmatching] = useState(false);
  const [onlyDifferences, setOnlyDifferences] = useState(false);
  const actions = matchActions(match);
  const summary = matchReportSummary(match);
  const tolerances = tolerancesText(match.tolerances);
  const rows = onlyDifferences ? match.lines.filter(isDifferenceLine) : match.lines;

  const unmatch = async (reason: string) => {
    await api.POST("/market/invoice-matches/{id}/unmatch", {
      params: { path: { id: match.id } },
      body: { version: match.version, reason },
    });
    await refresh();
    toast({ tone: "success", title: t("market.matches.done.unmatched") });
  };

  const columns: DataTableColumn<InvoiceMatchLine>[] = [
    {
      id: "product",
      header: t("market.fields.product"),
      hideable: false,
      exportValue: (row) =>
        row.productCode ? `${row.productCode} ${matchLineLabel(row)}` : matchLineLabel(row),
      cell: (row) => (
        <span className="grid gap-0.5">
          {row.productId ? (
            <Link
              to="/market/stok/$productId"
              params={{ productId: row.productId }}
              className="underline underline-offset-4"
            >
              {matchLineLabel(row)}
            </Link>
          ) : (
            <span>{matchLineLabel(row)}</span>
          )}
          {row.productCode ? (
            <span className="font-mono text-xs text-muted-foreground">{row.productCode}</span>
          ) : (
            <span className="text-xs text-muted-foreground">{t("market.matches.productless")}</span>
          )}
        </span>
      ),
    },
    {
      id: "ordered",
      header: t("market.matches.fields.ordered"),
      align: "right",
      exportValue: (row) => quantityExport(row.orderedQuantity),
      cell: (row) =>
        row.productId === null ? (
          <span className="text-xs text-muted-foreground">{t("market.matches.notInOrder")}</span>
        ) : (
          <span className="grid justify-items-end gap-0.5">
            <span className="tabular">{formatStockQuantity(row.orderedQuantity)}</span>
            <span className="text-xs text-muted-foreground">{formatUnitPrice(row.orderUnitPrice)}</span>
          </span>
        ),
    },
    {
      id: "received",
      header: t("market.matches.fields.received"),
      align: "right",
      exportValue: (row) => quantityExport(row.receivedQuantity),
      cell: (row) => (
        <span className="grid justify-items-end gap-0.5">
          <span className="tabular">{formatStockQuantity(row.receivedQuantity)}</span>
          <span className="text-xs text-muted-foreground">{formatUnitPrice(row.receiptUnitCost)}</span>
        </span>
      ),
    },
    {
      id: "invoiced",
      header: t("market.matches.fields.invoiced"),
      align: "right",
      exportValue: (row) => quantityExport(row.invoicedQuantity),
      cell: (row) => (
        <span className="grid justify-items-end gap-0.5">
          <span className="tabular">{formatStockQuantity(row.invoicedQuantity)}</span>
          <span className="text-xs text-muted-foreground">{formatUnitPrice(row.invoiceUnitPrice)}</span>
        </span>
      ),
    },
    {
      id: "quantityDifference",
      header: t("market.matches.fields.quantityDifference"),
      align: "right",
      exportValue: (row) => quantityExport(row.quantityDifference),
      cell: (row) => <span className="tabular">{formatStockQuantity(row.quantityDifference)}</span>,
    },
    {
      id: "priceDifference",
      header: t("market.matches.fields.priceDifference"),
      align: "right",
      exportValue: (row) => row.unitPriceDifference,
      cell: (row) => (
        <span className="tabular whitespace-nowrap">{formatUnitPrice(row.unitPriceDifference)}</span>
      ),
    },
    {
      id: "amountDifference",
      header: t("market.matches.fields.amountDifference"),
      align: "right",
      exportValue: (row) => moneyExport(row.amountDifference),
      cell: (row) => <MoneyText value={row.amountDifference} colorize />,
    },
    {
      id: "status",
      header: t("common.status"),
      exportValue: (row) => MATCH_LINE_STATUS_LABELS[row.status],
      cell: (row) => <MatchLineStatusBadge status={row.status} />,
    },
  ];

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">
          {match.invoiceNo ?? t("market.matches.noInvoiceNo")}
        </span>
        <InvoiceMatchStatusBadge status={match.status} />
        <StatusBadge tone={match.result === "MATCHED" ? "success" : "warning"}>
          {INVOICE_MATCH_RESULT_LABELS[match.result]}
        </StatusBadge>
        {match.automatic ? <StatusBadge tone="neutral">{t("market.matches.system")}</StatusBadge> : null}
        <span className="text-sm text-muted-foreground">{match.supplierName}</span>
        {permissions.canMatchInvoices && actions.unmatch ? (
          <Button variant="destructive" className="ms-auto" onClick={() => setUnmatching(true)}>
            <Undo2 aria-hidden="true" />
            {t("market.matches.unmatch")}
          </Button>
        ) : null}
      </div>

      {match.status === "PENDING_APPROVAL" ? (
        <Alert tone="info">{t("market.matches.pendingHint")}</Alert>
      ) : null}
      {match.status === "UNMATCHED" ? (
        <Alert tone="warning" title={t("market.matches.unmatchedTitle")}>
          {match.unmatchedReason ?? "—"}
        </Alert>
      ) : null}
      {match.automatic ? <Alert tone="info">{t("market.matches.automaticHint")}</Alert> : null}

      <Section title={t("market.matches.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("market.matches.fields.invoice")}>
            <Link
              to="/faturalar/$id"
              params={{ id: match.invoiceId }}
              className="underline underline-offset-4"
            >
              {match.invoiceNo ?? t("market.matches.noInvoiceNo")} · {formatDate(match.invoiceIssueDate)}
            </Link>
          </Field>
          <Field label={t("market.matches.fields.invoiceNet")}>
            <MoneyText value={match.invoiceNetAmount} />
          </Field>
          <Field label={t("market.matches.fields.amountDifference")}>
            <MoneyText value={match.amountDifference} colorize />
          </Field>
          <Field label={t("market.matches.fields.costAdjustment")}>
            <MoneyText value={match.costAdjustment} colorize />
          </Field>
          <Field label={t("market.matches.fields.tolerances")}>
            {t("market.matches.tolerancesValue", tolerances)}
          </Field>
          <Field label={t("market.matches.fields.matchedAt")}>
            {formatDateTime(match.matchedAt)}
            {match.automatic ? ` · ${t("market.matches.system")}` : ""}
          </Field>
          <Field label={t("market.matches.fields.approvedAt")}>{formatDateTime(match.approvedAt)}</Field>
          <Field label={t("market.matches.fields.differenceLines")}>{summary.differenceLines}</Field>
        </dl>
        <p className="text-xs text-muted-foreground">{t("market.matches.costHint")}</p>
      </Section>

      <Section title={t("market.matches.receiptsSection")}>
        <TableFrame label={t("market.matches.receiptsSection")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("market.receipts.fields.receiptNo")}</Th>
              <Th>{t("market.receipts.fields.despatchNo")}</Th>
              <Th>{t("market.fields.station")}</Th>
              <Th align="right">{t("market.receipts.fields.netAmount")}</Th>
              <Th>{t("common.status")}</Th>
            </tr>
          </thead>
          <tbody>
            {match.receipts.map((receipt) => (
              <tr key={receipt.receiptId} className="border-t">
                <td className="px-3 py-2">
                  <Link
                    to="/market/mal-kabul/$id"
                    params={{ id: receipt.receiptId }}
                    className="font-mono underline underline-offset-4"
                  >
                    {receipt.receiptNo}
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{receipt.despatchNo ?? "—"}</td>
                <td className="px-3 py-2">{receipt.stationName}</td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={receipt.netAmount} />
                </td>
                <td className="px-3 py-2">
                  {receipt.isActive ? (
                    <StatusBadge tone="success">{t("market.matches.receiptActive")}</StatusBadge>
                  ) : (
                    <StatusBadge tone="neutral">{t("market.matches.receiptReleased")}</StatusBadge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </Section>

      <Section title={t("market.matches.reportTitle")}>
        <dl className="grid gap-3 sm:grid-cols-4" data-testid="match-summary">
          <Field label={t("market.matches.fields.ordered")}>{formatStockQuantity(summary.ordered)}</Field>
          <Field label={t("market.matches.fields.received")}>{formatStockQuantity(summary.received)}</Field>
          <Field label={t("market.matches.fields.invoiced")}>{formatStockQuantity(summary.invoiced)}</Field>
          <Field label={t("market.matches.fields.amountDifference")}>
            <MoneyText value={summary.amountDifference} colorize />
          </Field>
        </dl>
        {summary.notOrderedLines > 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("market.matches.notOrderedHint", { count: summary.notOrderedLines })}
          </p>
        ) : null}
        {summary.consistent ? null : <Alert tone="warning">{t("market.matches.inconsistent")}</Alert>}
      </Section>

      <DataTable
        caption={t("market.matches.reportCaption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        emptyState={<p className="p-4 text-sm text-muted-foreground">{t("market.matches.noDifferences")}</p>}
        filters={
          <label className="flex min-h-10 items-center gap-2 text-sm">
            <Checkbox
              checked={onlyDifferences}
              onChange={(event) => setOnlyDifferences(event.target.checked)}
            />
            {t("market.matches.onlyDifferences")}
          </label>
        }
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: `fark-raporu-${match.invoiceNo ?? match.id}`,
            sheetName: t("market.matches.reportCaption"),
            columns: visible,
            rows: match.lines,
          });
        }}
      />

      {match.approvalRequestId ? (
        <Section title={t("market.matches.approval")}>
          <ApprovalRequestPanel requestId={match.approvalRequestId} showSummary={false} />
        </Section>
      ) : null}

      <ReasonDialog
        open={unmatching}
        onOpenChange={setUnmatching}
        title={t("market.matches.unmatchTitle")}
        description={t("market.matches.unmatchDescription")}
        confirmLabel={t("market.matches.unmatch")}
        onConfirm={unmatch}
      />
    </div>
  );
}
